// Frontend/src/types/issue.ts
//
// Canonical issue enums. The three issue pages each invented their own shape
// -- "Pending" vs "PENDING", "Low" vs "LOW", a free-text category string --
// which is why none of them could talk to the same API. These are the
// server's values, verbatim, and the display strings come from i18n.

export type IssueStatus = "PENDING" | "IN_PROGRESS" | "RESOLVED" | "CANCELLED";
export type IssuePriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

/**
 * Categories are rows the HOA admin owns, not a fixed enum. Every building
 * starts with a template set and the admin adds to it, so the frontend can
 * no longer know the list at build time -- it fetches it.
 */
export type IssueCategory = {
  id: string;
  /** Stable slug. Template keys are upper-case ("PLUMBING"); admin-created
   *  ones are kebab-case ("roof-leaks"). Never shown to a user. */
  key: string;
  name: string;
  icon: string;
  is_template: boolean;
  is_active: boolean;
  sort_order: number;
  issue_count: number;
};

export type CreateIssueCategoryData = {
  name: string;
  icon?: string;
};

/** Icons the server accepts. Anything else falls back to a default glyph. */
export const ISSUE_CATEGORY_ICONS = [
  "wrench", "droplets", "zap", "wind", "elevator", "building", "volume",
  "shield", "layers", "paint", "trash", "key", "wifi", "car", "leaf", "flame",
] as const;
export type IssueCategoryIcon = (typeof ISSUE_CATEGORY_ICONS)[number];

/** The list endpoint is paginated; everything else here returns a bare array. */
export type Paginated<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

export const ISSUES_PAGE_SIZE = 10;

export const ISSUE_STATUSES: IssueStatus[] = [
  "PENDING",
  "IN_PROGRESS",
  "RESOLVED",
  "CANCELLED",
];

export const ISSUE_PRIORITIES: IssuePriority[] = ["LOW", "MEDIUM", "HIGH", "URGENT"];

export type IssueUser = {
  id: string;
  email: string;
  full_name: string;
  role: string;
};

export type IssueAttachment = {
  id: string;
  /** Signed, expiring relative URL. Not a bare path -- a bare path 403s. */
  file: string | null;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_by_name: string | null;
  created_at: string;
};

export type IssueComment = {
  id: string;
  body: string;
  is_internal: boolean;
  author_name: string | null;
  created_at: string;
};

export type IssueStatusChange = {
  id: string;
  /** Empty string on the row that records creation. */
  from_status: IssueStatus | "";
  to_status: IssueStatus;
  note: string;
  changed_by_name: string | null;
  created_at: string;
};

export type IssueListItem = {
  id: string;
  title: string;
  /** The category's id, or null for an issue whose category was removed. */
  category: string | null;
  category_name: string | null;
  category_icon: string | null;
  category_key: string | null;
  status: IssueStatus;
  status_display: string;
  priority: IssuePriority;
  priority_display: string;
  unit: string | null;
  unit_code: string | null;
  building: string;
  building_name: string;
  reporter_name: string | null;
  assignee_name: string | null;
  attachment_count: number;
  comment_count: number;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
};

export type Issue = IssueListItem & {
  description: string;
  attachments: IssueAttachment[];
  comments: IssueComment[];
  status_changes: IssueStatusChange[];
  reporter: IssueUser | null;
  assignee: IssueUser | null;
};

export type IssueFilters = {
  status?: IssueStatus | "ALL";
  priority?: IssuePriority | "ALL";
  /** Category id or key. "ALL" means do not filter. */
  category?: string | "ALL";
  unit?: string;
  building?: string;
  search?: string;
  page?: number;
};

export type CreateIssueData = {
  title: string;
  description: string;
  /** Category id. Omit and the server files it under the building's General. */
  category?: string | null;
  priority: IssuePriority;
  unit?: string | null;
  building?: string | null;
  attachments?: File[];
  /** Admins only: creates a category for the building and files under it. */
  new_category_name?: string;
  new_category_icon?: string;
};

export type UpdateIssueData = {
  status?: IssueStatus;
  priority?: IssuePriority;
  category?: string;
  assignee?: string | null;
  status_note?: string;
};

/**
 * A row in the admin's "who is this about?" picker.
 *
 * Deliberately not the units page's `UnitList`: that type carries fees,
 * balances and occupancy status, none of which help you find an apartment,
 * and it has no resident names -- which are the thing an admin actually
 * searches by. Nobody remembers that the leak was in B-204; they remember it
 * was Maria's.
 */
export type IssueTargetResident = {
  id: string;
  name: string;
  email: string;
};

export type IssueTargetUnit = {
  id: string;
  unit_code: string;
  floor: number | null;
  residents: IssueTargetResident[];
  resident_count: number;
};

export type IssueStats = {
  total: number;
  open: number;
  by_status: Record<IssueStatus, number>;
  by_priority: Record<IssuePriority, number>;
};
